import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const CONSENT_KEY = "stratiq_consent";
const CONSENT_COOKIE = "stratiq_cookie_consent";
const EXPIRY_DAYS = 365;

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

function hasConsented() {
  return localStorage.getItem(CONSENT_KEY) || getCookie(CONSENT_COOKIE);
}

function storeConsent(value) {
  localStorage.setItem(CONSENT_KEY, value);
  setCookie(CONSENT_COOKIE, value, EXPIRY_DAYS);
}

// Cute cookie SVG icon - simplified, no animations that cause overflow
function CookieIcon({ size = 48 }) {
  return (
    <svg 
      viewBox="0 0 64 64" 
      width={size} 
      height={size} 
      style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.3))" }}
    >
      {/* Cookie base - warm brown */}
      <circle cx="32" cy="32" r="28" fill="#D4A574" />
      {/* Cookie darker edge for depth */}
      <circle cx="32" cy="32" r="28" fill="none" stroke="#B8956A" strokeWidth="3" />
      {/* Chocolate chips */}
      <ellipse cx="20" cy="22" rx="5" ry="4" fill="#5D4037" transform="rotate(-15 20 22)" />
      <ellipse cx="42" cy="18" rx="4" ry="3.5" fill="#5D4037" transform="rotate(20 42 18)" />
      <ellipse cx="26" cy="38" rx="4.5" ry="4" fill="#5D4037" transform="rotate(-10 26 38)" />
      <ellipse cx="44" cy="36" rx="5" ry="4" fill="#5D4037" transform="rotate(15 44 36)" />
      <ellipse cx="34" cy="26" rx="3.5" ry="3" fill="#5D4037" transform="rotate(5 34 26)" />
      <ellipse cx="18" cy="44" rx="3" ry="2.5" fill="#5D4037" transform="rotate(-20 18 44)" />
      {/* Cookie crumbs/texture */}
      <circle cx="14" cy="30" r="2" fill="#C49A6C" />
      <circle cx="48" cy="28" r="1.5" fill="#C49A6C" />
      <circle cx="38" cy="46" r="2" fill="#C49A6C" />
      {/* Highlight for 3D effect */}
      <ellipse cx="24" cy="16" rx="8" ry="4" fill="rgba(255,255,255,0.15)" transform="rotate(-30 24 16)" />
    </svg>
  );
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (hasConsented()) return;
    // Show immediately - no delay
    setVisible(true);
  }, []);

  const accept = (type) => {
    storeConsent(type);
    setVisible(false);
  };

  if (!visible) return null;

  return createPortal(
    <div className="fixed bottom-4 left-4 z-[9997]" data-testid="cookie-consent">
      <AnimatePresence mode="wait">
        {!expanded ? (
          /* Collapsed: Just the cookie icon */
          <motion.button
            key="cookie-icon"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={() => setExpanded(true)}
            className="cursor-pointer"
            data-testid="cookie-widget-collapsed"
            aria-label="Cookie preferences"
          >
            <CookieIcon size={52} />
          </motion.button>
        ) : (
          /* Expanded: Cookie widget with options */
          <motion.div
            key="cookie-expanded"
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ duration: 0.2 }}
            className="bg-slate-900/95 border border-white/[0.1] rounded-2xl p-4 shadow-2xl shadow-black/50 max-w-[260px]"
            data-testid="cookie-widget-expanded"
          >
            {/* Header with cookie icon */}
            <div className="flex items-center gap-3 mb-3">
              <CookieIcon size={32} />
              <div>
                <h3 className="text-sm font-bold text-white">Cookies</h3>
                <p className="text-[10px] text-white/40">We respect your privacy</p>
              </div>
            </div>

            {/* Brief text */}
            <p className="text-[11px] text-white/50 leading-relaxed mb-3">
              We use cookies for analytics.{" "}
              <Link to="/legal/cookies" className="text-cyan-400/70 hover:text-cyan-400 underline">
                Learn more
              </Link>
            </p>

            {/* Buttons */}
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => accept("accepted")} 
                data-testid="cookie-accept"
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full px-4 py-2 text-xs font-bold hover:from-amber-400 hover:to-orange-400 transition-all"
              >
                Accept All
              </button>
              <button 
                onClick={() => accept("essential")} 
                data-testid="cookie-essential-only"
                className="w-full bg-white/[0.06] text-white/60 border border-white/[0.1] rounded-full px-4 py-1.5 text-xs font-medium hover:text-white transition-all"
              >
                Essential Only
              </button>
            </div>

            {/* Close button */}
            <button 
              onClick={() => setExpanded(false)}
              className="absolute -top-2 -right-2 w-5 h-5 bg-slate-800 border border-white/10 rounded-full flex items-center justify-center text-white/40 hover:text-white text-xs"
              aria-label="Minimize"
            >
              &times;
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body
  );
}
